import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Avatar,
  Checkbox,
  Button,
  Typography,
  Box,
} from "@mui/material";
import { colorPrimary } from "../../Styles/Colors";

const ListDialog = ({
  open = true,
  onClose,
  title = "Dialog Title",
  description = "A dialog is a type of modal window that appears in front of app content to provide critical information, or prompt for a decision to be made.",
  items = [
    { id: 1, text: "List Item", percentage: "100%", avatar: "A" },
    { id: 2, text: "List Item", percentage: "100%", avatar: "A" },
    { id: 3, text: "List Item", percentage: "100%", avatar: "A" },
  ],
  primaryAction = "Action 1",
  secondaryAction = "Action 2",
  onPrimaryAction,
  onSecondaryAction,
}) => {
  const [selectedItems, setSelectedItems] = useState(new Set());

  const handleItemSelect = (itemId) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId);
    } else {
      newSelected.add(itemId);
    }
    setSelectedItems(newSelected);
  };

  const handlePrimaryAction = () => {
    if (onPrimaryAction) {
      onPrimaryAction(Array.from(selectedItems));
    }
    if (onClose) onClose();
  };

  const handleSecondaryAction = () => {
    if (onSecondaryAction) {
      onSecondaryAction(Array.from(selectedItems));
    }
    if (onClose) onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "28px",
          minHeight: 450,
          minWidth: 280,
          maxWidth: 560,
          maxHeight: "80vh",
          background: "var(--SMA-white, #FFF)",
          boxShadow:
            "0 1px 2px 0 rgba(0, 0, 0, 0.30), 0 2px 6px 2px rgba(0, 0, 0, 0.15)",
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 1,
          fontSize: "1.25rem",
          fontWeight: 500,
          color: colorPrimary,
        }}
      >
        {title}
      </DialogTitle>

      <DialogContent sx={{ pt: 0 }}>
        <DialogContentText
          sx={{
            mb: 3,
            color: "text.secondary",
            lineHeight: 1.6,
          }}
        >
          {description}
        </DialogContentText>

        <List
          sx={{
            border: 1,
            borderColor: "divider",
            borderRadius: 0,
            bgcolor: "background.paper",
            overflow: "hidden",
            border: "none",
          }}
        >
          {items.map((item, index) => (
            <ListItem
              key={item.id}
              button
              onClick={() => handleItemSelect(item.id)}
              selected={selectedItems.has(item.id)}
              divider={index !== items.length - 1}
              sx={{
                py: 1.5,
                px: 2,
                "&.Mui-selected": {
                  bgcolor: "primary.light",
                  "&:hover": {
                    bgcolor: "primary.light",
                  },
                },
                "&:hover": {
                  bgcolor: "action.hover",
                },
                transition: "background-color 0.2s ease",
              }}
            >
              <ListItemAvatar>
                <Avatar
                  sx={{
                    bgcolor: "#9c27b0",
                    width: 36,
                    height: 36,
                    fontSize: "0.875rem",
                    fontWeight: 500,
                  }}
                >
                  {item.avatar || "A"}
                </Avatar>
              </ListItemAvatar>

              <ListItemText
                primary={item.text}
                sx={{
                  flex: 1,
                  "& .MuiListItemText-primary": {
                    fontSize: "0.875rem",
                    fontWeight: 400,
                  },
                }}
              />

              <Box
                sx={{
                  mr: 3,
                  minWidth: "fit-content",
                }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: 400,
                  }}
                >
                  {item.percentage}
                </Typography>
              </Box>

              <Checkbox
                checked={selectedItems.has(item.id)}
                onChange={() => handleItemSelect(item.id)}
                color="primary"
                size="small"
                sx={{
                  "& .MuiSvgIcon-root": {
                    fontSize: "24px",
                    color: colorPrimary,
                  },
                }}
              />
            </ListItem>
          ))}
        </List>
      </DialogContent>

      <DialogActions
        sx={{
          p: 3,
          gap: 1,
          justifyContent: "flex-end",
        }}
      >
        <Button
          onClick={handleSecondaryAction}
          variant="outlined"
          size="medium"
          sx={{
            textTransform: "none",
            fontWeight: 500,
            px: 3,
            backgroundColor: "transparent",
            border: "none",
            color: colorPrimary,
          }}
        >
          {secondaryAction}
        </Button>
        <Button
          onClick={handlePrimaryAction}
          variant="contained"
          size="medium"
          sx={{
            textTransform: "none",
            fontWeight: 500,
            px: 3,
            backgroundColor: "transparent",
            border: "none",
            color: colorPrimary,
            boxShadow: "none",
            "&:hover": {
              boxShadow: "none",
            },
          }}
        >
          {primaryAction}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ListDialog;
